import * as React from 'react';
import {useDispatch} from 'react-redux';
import {getDocs, onSnapshot, query, setDoc, where} from 'firebase/firestore';
import {useAuth} from './AuthContext';
import {playlistDoc, playlistsCol} from '../firebase/firestore';
import store from '../store/store';
import {setAllPlaylists, UserPlaylist} from '../store/slices/userPlaylists/reducers';

/**
 * Keeps local userPlaylists state in sync with Firestore for the signed-in user.
 *
 * On sign-in:
 *   1. Fetch the user's playlists (where ownerId == uid).
 *   2. If Firestore is empty AND local has guest playlists → stamp ownerId + default visibility,
 *      upload them (one-time migration).
 *   3. Otherwise replace local state with Firestore (Firestore wins across devices).
 *   4. Subscribe via onSnapshot for live updates.
 *
 * On sign-out: clear local state so the next user starts clean.
 *
 * Outgoing writes (create / edit / delete / visibility change) are mirrored via playlistsSyncMiddleware.
 */
export const FirestorePlaylistSync: React.FC = () => {
    const {user, status} = useAuth();
    const dispatch = useDispatch();
    const prevUidRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (status === 'loading') return;

        const uid = user?.uid ?? null;
        const prevUid = prevUidRef.current;
        prevUidRef.current = uid;

        // Signed out (or was never signed in) — clear anything left behind when transitioning user → guest.
        if (!uid) {
            if (prevUid) {
                dispatch(setAllPlaylists({playlists: {}, order: []}));
            }
            return;
        }

        const ownedQuery = query(playlistsCol, where('ownerId', '==', uid));
        let unsubscribe: (() => void) | null = null;
        let cancelled = false;

        (async () => {
            try {
                const snapshot = await getDocs(ownedQuery);
                if (cancelled) return;

                const localState = store.getState().userPlaylists;
                const localPlaylists = Object.values(localState.playlists);

                if (snapshot.empty && localPlaylists.length > 0) {
                    // First-ever sign-in with guest playlists — stamp ownership and upload.
                    const stamped = localPlaylists.map(pl => ({
                        ...pl,
                        ownerId: uid,
                        visibility: pl.visibility ?? 'private',
                    }));
                    await Promise.all(stamped.map(pl => setDoc(playlistDoc(pl.id), pl)));
                    // onSnapshot below will mirror these back into Redux with Firestore as the source of truth.
                } else {
                    applySnapshot(snapshot.docs.map(d => d.data() as UserPlaylist));
                }

                if (cancelled) return;
                unsubscribe = onSnapshot(ownedQuery, snap => {
                    applySnapshot(snap.docs.map(d => d.data() as UserPlaylist));
                });
            } catch (err) {
                console.error('Firestore playlist sync failed', err);
            }
        })();

        function applySnapshot(playlists: UserPlaylist[]) {
            // Newest first — matches the `unshift` behavior of createPlaylist.
            const sorted = [...playlists].sort((a, b) => b.createdAt - a.createdAt);
            const byId: Record<string, UserPlaylist> = {};
            const order: string[] = [];
            for (const pl of sorted) {
                byId[pl.id] = pl;
                order.push(pl.id);
            }
            dispatch(setAllPlaylists({playlists: byId, order}));
        }

        return () => {
            cancelled = true;
            unsubscribe?.();
        };
    }, [user, status, dispatch]);

    return null;
};
